<template>
  <div>
    <button
      type="button"
      class="btn btn-outlined btn-secondary"
      @click="onClick"
    >
      View route
    </button>

    <zb-modal
      ref="detailModal"
      class="itinerary-details-modal slide-in-modal"
      :no-padding="true"
    >
      <template #title>
        <div class="mb-8 px-10">
          <h2 class="pt-6 fs-32 font-bold font-bry">
            {{ trip.name }}
          </h2>
          <p class="text-black-light-5 fs-16">
            {{ trip.itinerary_destination_text }}
          </p>
        </div>
      </template>

      <template v-slot:default>
        <div class="px-10">
          <h4 class="fs-20 font-bold mb-6 font-bry">Details</h4>
          <p class="mb-4">
            <strong>Length:</strong>
            {{ minLength }} to {{ maxLength }} nights
          </p>
          <p class="mb-4" v-html="season" />

          <div class="standard-list mb-4">
            <p class="highlight mb-4"><strong>Highlights:</strong></p>
            <div v-html="trip.highlights" />
          </div>
          <p class="mb-3">
            <strong>Start:</strong>
            {{ trip.start }}
          </p>

          <p class="mb-4">
            <strong>Finish:</strong>
            {{ trip.finish }}
          </p>

          <p v-if="description">
            {{ description }}
          </p>
          <hr class="mb-8 mt-6 border border-black-30" />

          <img
            :src="trip.itinerary_map"
            class="room-image w-full h-full cover-image rounded-bl rounded-tl"
          />

          <hr class="my-8 border border-black-30" />

          <div class="mt-6">
            <h4 class="fs-20 font-bold mb-8 font-bry">Itinerary highlights</h4>
            <div
              class="yacht-activity"
              v-for="(activity, idx) in activities"
              :key="activity.id"
            >
              <div class="flex-1 yacht-activity__thumb">
                <img :src="activity.activity_image" />
              </div>
              <div class="flex-1 yacht-activity__body">
                <h5
                  class="fs-20 font-bold mb-4 font-bry"
                  v-text="`${idx + 1}. ${activity.name}`"
                />
                <div class="standard-list" v-html="activity.activity_text" />
              </div>
            </div>
            <hr class="mb-8 border border-black-30" />
          </div>

          <div class="mt-6 standard-list" v-if="additionalInformation">
            <h4 class="fs-20 font-bold mb-8 font-bry">
              Additional information
            </h4>
            <p class="mb-4" v-html="additionalInformation" />
            <hr class="my-8 border border-black-30" />
          </div>

          <div class="my-6">
            <h4 class="fs-20 font-bold mb-6">Boat amenities</h4>
            <div>
              <template v-if="included.length">
                <h5 class="font-bold my-3" v-if="included.length">Included</h5>
                <inclusion-sub-list :inclusions="included" />
              </template>

              <template v-if="notIncluded.length">
                <hr class="border border-black-30 my-6" />
                <h5 class="font-bold my-3">Not Included</h5>
                <inclusion-sub-list :inclusions="notIncluded" />
              </template>

              <template v-if="extra.length">
                <hr class="border border-black-30 my-6" />
                <h5 class="font-bold my-3">Additional cost</h5>
                <inclusion-sub-list :inclusions="extra" />
              </template>
            </div>
          </div>
        </div>
      </template>
      <template #stickyFooter>
        <footer class="p-6 text-right sm:text-left border-t border-black-30">
          <div class="flex items-center">
            <div class="flex-1 mr-4">
              from
              <span class="font-bold fs-24 font-bry">
                <span class="fs-24">
                  <currency-context>
                    <template v-slot="{ convert }">
                      <span v-text="convert(itinerary.price_per_night)"></span>
                    </template>
                  </currency-context>
                </span>
              </span>
              <span>/night</span>
            </div>

            <div class="flex">
              <button
                type="button"
                class="btn btn-primary ml-4 w-full enquire-button"
                @click="handleEnquireClicked"
              >
                Enquire
              </button>
            </div>
          </div>
        </footer>
      </template>
    </zb-modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import SeasonMixin from "./SeasonMixin";

import ZbModal from "../../components/core/ZbModal";
import InclusionSubList from "../../components/core/InclusionSubList";
import { YACHT_FACILITIES } from "../../helpers/constants";
import CurrencyContext from "../../components/core/CurrencyContext";

export default {
  mixins: [SeasonMixin],
  components: { ZbModal, InclusionSubList, CurrencyContext },
  props: ["itinerary"],
  filters: {
    formatDate(timeString, format) {
      return moment(timeString).format(format);
    },
    formatTime(timeString) {
      return moment(timeString, "HH:mm:ss").format("hh:mma");
    },
  },
  data() {
    return {
      destinationData: null,
    };
  },
  computed: {
    ...mapState(["supplier"]),
    inclusions() {
      return get(this.supplier, "inclusions", []).filter(
        (incl) => incl.list_id === YACHT_FACILITIES
      );
    },
    orderedInclusions() {
      return orderBy(this.inclusions, ["order", "name"], ["asc", "asc"]);
    },
    included() {
      return this.orderedInclusions.filter((incl) => incl.type === "included");
    },
    extra() {
      return this.orderedInclusions.filter((incl) => incl.type === "extra");
    },
    notIncluded() {
      return this.orderedInclusions.filter(
        (incl) => incl.type === "not_included"
      );
    },
    activities() {
      return get(this.itinerary, "charter_itinerary.activities", []);
    },
    minLength() {
      return this.itinerary.min_length || this.trip.min_length;
    },
    maxLength() {
      return this.itinerary.max_length || this.trip.max_length;
    },
    trip() {
      return this.itinerary.charter_itinerary;
    },
    description() {
      const desOverride = get(this.itinerary, "description_override", null);

      if (desOverride) {
        return desOverride;
      }

      const desc = get(this.itinerary, "charter_itinerary.description", null);

      return desc;
    },
    additionalInformation() {
      return get(
        this.itinerary,
        "charter_itinerary.additional_information",
        null
      );
    },
    destinationLinks() {
      let urls = [];

      for (const destinationSKU in this.destinationData) {
        urls.push(this.destinationData[destinationSKU].url);
      }

      return urls;
    },
    returnDate() {
      const startDate = moment(this.trip.departure_date);
      const end = startDate.add(this.trip.itineraries.itinerary_length, "days");

      return end.format("DD MMM YYYY");
    },
    season() {
      let out = this.convert(this.monthNumbers);
      out = isEmpty(out) ? "Year round" : out;
      return "<b>Season:</b> " + out;
    },
    monthNumbers() {
      try {
        let seasons = JSON.parse(this.itinerary.season);
        return seasons
          .map((monthName) =>
            Number(
              moment()
                .month(monthName)
                .format("M")
            )
          )
          .sort((a, b) => a - b);
      } catch (error) {
        return [];
      }
    },
  },
  mounted() {
    if (this.trip.available === "Sold out") {
      return;
    }

    let destinations = "";

    try {
      destinations = JSON.parse(this.trip.itineraries.destination).join(",");
    } catch (error) {}

    if (!destinations) {
      return;
    }

    $.ajax({
      method: "POST",
      url: "/",
      data: {
        destination_ids: destinations,
        action: "zuBlu/public/getDestinationsDivingEnvironment",
        [window.Craft.csrfTokenName]: window.Craft.csrfTokenValue,
      },
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    }).done((response) => {
      try {
        this.destinationData = JSON.parse(response);
      } catch (error) {}
    });
  },
  methods: {
    onClick() {
      this.$refs.detailModal.show();

      this.$el.querySelector(".zb-modal-scrollable-content").scrollTop = 0;
    },
    handleEnquireClicked() {
      this.$refs.detailModal.hide();

      this.$nextTick(() => {
        this.$emit("enquire");
      });
    },
  },
};
</script>

<style>
.itinerary-details-modal .zb-modal-content {
  width: 780px;
}

@media (min-width: 601px) and (max-width: 1200px) {
  .itinerary-details-modal .zb-modal-content {
    max-width: 100%;
    width: auto;
    margin-left: 40px;
    margin-right: 40px;
  }
}
</style>
<style scoped>
.highlight ::v-deep div {
  display: inline;
}

.yacht-activity {
  display: flex;
  margin-bottom: 64px;
}

.yacht-activity__thumb {
  margin-bottom: 0px;
  margin-right: 32px;
}

.enquire-button {
  min-width: 6.75rem;
}

@media (max-width: 600px) {
  .itinerary-detail-button {
    height: 48px;
    width: 100%;
  }

  .yacht-activity {
    flex-direction: column;
  }

  .yacht-activity__thumb {
    margin-bottom: 32px;
    margin-right: 0;
  }

  .yacht-activity:nth-child(even) {
    flex-direction: column;
  }
}
</style>
